// Mera Naksha — Our Story / Vision & Mission
// Long-form institutional narrative. Layout is intentionally editorial: a
// single reading column with strong typographic hierarchy, generous
// whitespace, and no marketing CTAs in the middle so the message reads like
// a team letter, not a landing page. Brand-first identity — no individual
// names are surfaced publicly per the Institutional Branding directive.
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Home as HomeIcon, // eslint-disable-line no-unused-vars
  ArrowLeft,
  Download,
  Sparkles,
  Building2,
  Compass,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import LanguageToggle from "@/components/LanguageToggle";
import LegalDisclaimer from "@/components/LegalDisclaimer";
import BrandLogo from "@/components/BrandLogo";
import { t } from "@/i18n";

const COMMUNITY_URL = "https://chat.whatsapp.com/JlGqzsbBywI1tQAEpqfaJk";

// Bullet list block — bilingual-aware
const BulletList = ({ items }) => (
  <ul className="mt-3 space-y-2 text-[14.5px] leading-relaxed text-foreground/85">
    {items.map((line) => (
      <li key={line} className="flex items-start gap-2.5">
        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-gold-dark shrink-0" />
        <span>{line}</span>
      </li>
    ))}
  </ul>
);

const SectionHeader = ({ kicker, title, Icon }) => (
  <div className="flex items-start gap-3">
    {Icon && (
      <div className="h-11 w-11 rounded-2xl bg-navy text-gold grid place-items-center shrink-0 shadow-sm">
        <Icon className="h-5 w-5" strokeWidth={2.25} />
      </div>
    )}
    <div className="min-w-0">
      <div className="text-[10.5px] uppercase tracking-[0.22em] text-gold-dark font-extrabold">
        {kicker}
      </div>
      <h2 className="mt-1 text-[24px] sm:text-[30px] font-extrabold tracking-tight text-navy leading-[1.15]">
        {title}
      </h2>
    </div>
  </div>
);

export default function AboutUs() {
  const [lang, setLang] = useState(
    typeof window !== "undefined" && window.localStorage.getItem("mn_lang")
      ? window.localStorage.getItem("mn_lang")
      : "en"
  );
  const setLangPersisted = (l) => {
    setLang(l);
    try { window.localStorage.setItem("mn_lang", l); } catch (e) {
      if (process.env.NODE_ENV !== "production") console.warn("lang persist failed", e);
    }
  };

  // The narrative is intentionally long-form and reads like an institutional
  // team letter. We keep one source of truth (English) — verbatim text — and
  // provide a condensed Hindi summary for readers who prefer Hindi. The full
  // English story is always available via the language toggle.
  return (
    <div className="min-h-screen bg-sand" data-testid="about-us-page">
      {/* Header */}
      <header
        className="sticky top-0 z-30 backdrop-blur-md bg-white/90 border-b border-border"
        data-testid="about-header"
      >
        <div className="mx-auto max-w-md sm:max-w-3xl px-4 h-16 flex items-center justify-between">
          <BrandLogo size="md" to="/" withTagline lang={lang} testId="about-brand-logo" />
          <div className="flex items-center gap-2">
            <Link
              to="/store"
              data-testid="about-nav-store"
              className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-gold text-navy text-xs font-bold border border-gold-dark hover:bg-gold-dark hover:text-white transition-colors"
            >
              <Download className="h-3.5 w-3.5" />
              <span>{t(lang, "store_kicker")}</span>
            </Link>
            <a
              href={COMMUNITY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-nav-community"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-whatsapp text-white text-xs font-bold shadow-[0_6px_14px_rgba(37,211,102,0.35)] hover:bg-whatsapp-hover transition-colors"
            >
              <FaWhatsapp className="h-4 w-4" />
              <span>{t(lang, "community_nav")}</span>
            </a>
            <LanguageToggle lang={lang} onChange={setLangPersisted} />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-md sm:max-w-3xl px-4 pt-8 pb-20">
        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-[12.5px] font-bold text-muted-foreground hover:text-navy"
          data-testid="about-back-link"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to home
        </Link>

        {/* Hero — editorial */}
        <section className="mt-5 rounded-3xl bg-gradient-to-br from-navy via-navy-deep to-navy text-white p-7 sm:p-10 relative overflow-hidden" data-testid="about-hero">
          <div className="absolute -right-10 -top-10 w-60 h-60 rounded-full bg-gold/15 blur-3xl pointer-events-none" />
          <div className="absolute -left-12 bottom-0 w-48 h-48 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="text-[11px] uppercase tracking-[0.28em] text-gold font-extrabold">
              Our Story
            </div>
            <h1 className="mt-3 text-[32px] sm:text-[44px] font-extrabold tracking-tight leading-[1.05]">
              Designing the Future of{" "}
              <span className="text-gold">Bharat</span>
            </h1>
            <p className="mt-4 text-[14.5px] sm:text-[15.5px] leading-relaxed text-white/85 max-w-prose">
              A letter from Team Mera Naksha on why we are building the digital
              infrastructure for Bharat's residential construction ecosystem.
            </p>
            <div className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold text-navy text-[11.5px] font-extrabold">
              <Sparkles className="h-3 w-3" /> Built by Team Mera Naksha
            </div>
          </div>
        </section>

        {/* 1) Designing the Future of Bharat — the emotional narrative */}
        <section className="mt-10" data-testid="story-narrative">
          <SectionHeader
            kicker="The Problem"
            title="Designing the Future of Bharat"
            Icon={Building2}
          />
          <div className="mt-5 space-y-4 text-[15px] leading-relaxed text-foreground/90 max-w-prose">
            <p>
              In the small towns and villages of India — from Gorakhpur to Buxar
              to Satara — a house is not just a building. It is the single
              largest financial investment most families will ever make in their
              lifetime. For many families, the combined cost of land and
              construction ranges from <span className="font-bold text-navy">₹50 lakh to ₹1 crore</span> or more.
              It represents decades of savings, hard work, family sacrifice, and
              often high-interest loans.
            </p>
            <p>
              Yet despite such a massive investment, millions of families still
              build homes without professional planning or intelligent design.
              Most people depend on local mistries, informal contractors, and
              unorganized construction methods because professional design
              services remain concentrated in metro, Tier-1, and Tier-2 cities.
              Meanwhile, more than <span className="font-bold text-navy">60% of India's population</span> living
              in smaller towns and rural regions remains underserved in quality
              building design and planning.
            </p>
            <p>The result is visible across India:</p>
            <BulletList
              items={[
                "Poor space utilization",
                "Wasted construction cost",
                "Improper ventilation and lighting",
                "Unsafe layouts",
                "Structural inefficiencies",
                "Dead spaces with no functional value",
                "Buildings that generate little or no long-term income for families",
              ]}
            />
            <p>
              Most importantly, many families are unaware that intelligent
              planning and professional design can significantly improve the
              financial value of their property by:
            </p>
            <BulletList
              items={[
                "Reducing construction cost",
                "Saving land and materials",
                "Increasing rental income opportunities",
                "Improving functionality and comfort",
                "Enhancing safety and durability",
                "Increasing long-term property value",
              ]}
            />
            <p>
              At the same time, India produces thousands of talented designers,
              engineers, planners, and construction professionals every year who
              struggle with limited opportunities, geographic restrictions, and
              fragmented markets. We believe <span className="font-bold text-navy">technology can solve both problems together.</span>
            </p>
          </div>
        </section>

        {/* 2) Our Vision */}
        <section className="mt-12" data-testid="story-vision">
          <SectionHeader
            kicker="Our Vision"
            title="The digital infrastructure for Bharat's construction ecosystem"
            Icon={Compass}
          />
          <div className="mt-5 rounded-3xl bg-white border-2 border-gold/30 p-6 sm:p-8 shadow-sm">
            <div className="space-y-4 text-[15px] leading-relaxed text-foreground/90 max-w-prose">
              <p>
                Mera Naksha is building the <span className="font-bold text-navy">digital infrastructure for Bharat's residential construction ecosystem.</span>{" "}
                We envision an India where every building — from a small rural
                home to a modern urban development — is planned with
                intelligence, efficiency, safety, and dignity.
              </p>
              <p>
                Through AI-powered platforms, standardized design systems,
                automation, remote collaboration, and digital workflows, we are
                creating a technology-driven ecosystem that digitally connects
                <span className="font-bold text-navy"> homeowners, designers, engineers, planners, contractors, and construction professionals</span> across India.
              </p>
              <p>
                Our vision is to democratize professional building design and
                make affordable, high-quality, execution-ready planning
                accessible to every Indian family regardless of location or
                income level. We believe good design should not remain a luxury
                available only in large cities. It should become a{" "}
                <span className="font-bold text-navy">national standard.</span>
              </p>
              <p>By optimizing how homes are planned and built, India can:</p>
              <BulletList
                items={[
                  "Save valuable land",
                  "Reduce material wastage",
                  "Lower construction costs",
                  "Improve sustainability",
                  "Increase rental and commercial income opportunities",
                  "Build safer and more efficient structures",
                  "Improve the quality of life for millions of families",
                ]}
              />
              <p>
                We also believe the future of India's construction economy will
                be driven by digitally connected professionals rather than
                geographically limited services. Mera Naksha aims to create{" "}
                <span className="font-bold text-navy">large-scale employment and entrepreneurial opportunities</span>{" "}
                for designers, engineers, planners, and construction
                professionals by enabling them to serve customers across India
                without needing to permanently relocate.
              </p>
            </div>
          </div>
        </section>

        {/* 3) Our Mission */}
        <section className="mt-12" data-testid="story-mission">
          <SectionHeader
            kicker="Our Mission"
            title="The trusted planning and design layer for residential construction"
            Icon={Building2}
          />
          <div className="mt-5 rounded-3xl bg-gold/10 border-2 border-gold/50 p-6 sm:p-8">
            <div className="space-y-4 text-[15px] leading-relaxed text-foreground/90 max-w-prose">
              <p>
                Our mission is to become the{" "}
                <span className="font-bold text-navy">trusted planning and design layer for residential construction across Bharat.</span>{" "}
                We are building a scalable technology platform that transforms
                how Indian families plan, design, and execute homes by combining:
              </p>
              <BulletList
                items={[
                  "AI-driven planning systems",
                  "Standardized workflows",
                  "Intelligent design optimization",
                  "Digital collaboration",
                  "Remote professional services",
                  "Execution-ready planning solutions",
                ]}
              />
              <p>
                We are not just creating building plans. We are helping families
                convert their homes from <span className="italic">emotional liabilities</span>{" "}
                into <span className="font-bold text-navy">long-term financial assets</span>{" "}
                by maximizing:
              </p>
              <BulletList
                items={[
                  "Space efficiency",
                  "Rental potential",
                  "Functional utility",
                  "Construction value",
                  "Long-term usability",
                ]}
              />
              <p>Our mission is to:</p>
              <BulletList
                items={[
                  "Make professional design accessible to underserved India",
                  "Reduce wastage of land, materials, time, and money",
                  "Bring transparency and professionalism into residential construction",
                  "Enable smarter and more sustainable building practices",
                  "Create scalable economic opportunities for design and construction professionals",
                  "Build trust-driven digital infrastructure for Bharat's housing ecosystem",
                ]}
              />
              <p>
                India will build <span className="font-bold text-navy">billions of square feet</span>{" "}
                in the coming decades. Mera Naksha aspires to become the
                platform that powers how Bharat plans, designs, and builds its
                future — combining design, technology, AI, and execution to
                create a smarter, more efficient, and better-designed nation.
              </p>
            </div>
          </div>
        </section>

        {/* 4) Sign-off — institutional / brand-first */}
        <section
          className="mt-12 rounded-3xl bg-navy text-white p-7 sm:p-10 relative overflow-hidden"
          data-testid="team-signoff"
        >
          <div className="absolute -right-8 -bottom-8 w-44 h-44 rounded-full bg-gold/15 blur-3xl pointer-events-none" />
          <div className="relative flex items-start gap-4">
            <div className="h-16 w-16 rounded-2xl bg-gold text-navy grid place-items-center shrink-0 font-extrabold text-xl shadow-md">
              MN
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-gold font-extrabold">
                Team Mera Naksha
              </div>
              <div className="mt-1 text-[22px] sm:text-[26px] font-extrabold tracking-tight leading-tight">
                Designing Better Homes for Bharat
              </div>
              <div className="text-[13.5px] text-white/80 mt-1">
                Built by Team Mera Naksha · Designed for Indian Families
              </div>
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-gold text-[11.5px] font-extrabold border border-gold/30">
                <Sparkles className="h-3 w-3" />
                Building the digital infrastructure for Bharat
              </div>
            </div>
          </div>
        </section>

        {/* Legal-safe disclaimer */}
        <div className="mt-10">
          <LegalDisclaimer lang={lang} variant="block" testId="about-legal-disclaimer" />
        </div>
      </main>

      {/* Footer (compact) */}
      <footer
        className="bg-navy text-white/85 mt-4 pt-8 pb-12 px-4"
        data-testid="about-footer"
      >
        <div className="mx-auto max-w-3xl">
          <div className="text-[11px] uppercase tracking-[0.22em] text-gold font-bold">
            Mera Naksha
          </div>
          <div className="text-[13.5px] font-bold text-white mt-1">
            {t(lang, "brand_tagline")}
          </div>
          <div className="mt-3 text-[12px] text-white/85 italic max-w-prose">
            {t(lang, "mission_snippet")}
          </div>
          <div className="mt-4 pt-4 border-t border-white/10">
            <LegalDisclaimer lang={lang} variant="footer" />
          </div>
          {/* Government registration credentials — required on every page
              now that Mera Naksha is a govt-registered entity. */}
          <div
            className="mt-4 pt-4 border-t border-white/10 text-[11px] text-white/70 leading-relaxed"
            data-testid="about-footer-legal-credentials"
          >
            <div className="text-[10px] uppercase tracking-[0.22em] text-gold/80 font-extrabold mb-1">
              Legal Credentials
            </div>
            <div>
              <span className="text-white/45">GSTIN:</span>{" "}
              <span className="font-mono font-bold text-white/80">06DUWPK7336G1Z2</span>
            </div>
            <div className="mt-0.5">
              <span className="text-white/45">Udyam Reg:</span>{" "}
              <span className="font-mono font-bold text-white/80">UDYAM-HR-03-0161073</span>
            </div>
            <div className="mt-0.5 max-w-prose">
              <span className="text-white/45">Registered Address:</span>{" "}
              T12-601, RPS Savana, Sector-88, Faridabad, Haryana, 121002
            </div>
          </div>
          <div className="mt-5 text-[11.5px] text-white/55">
            © {new Date().getFullYear()} Mera Naksha
          </div>
        </div>
      </footer>
    </div>
  );
}
